import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { fetchPhotoExports, retryPhotoExport, retryAllPhotoExports } from '../../api';
import './ExportStatusPanel.css';

const STATUS_ICONS = {
  pending: '...',
  in_progress: '...',
  success: 'OK',
  failed: 'X',
  started: '...',
  cancelled: '—',
};

const STATUS_CLASSES = {
  pending: 'status-pending',
  in_progress: 'status-progress',
  success: 'status-success',
  failed: 'status-failed',
  started: 'status-pending',
  cancelled: 'status-cancelled',
};

function formatDateTime(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function ExportHistoryItem({ item }) {
  return (
    <li className={`export-history-item ${STATUS_CLASSES[item.status]}`}>
      <span className="history-number">#{item.attempt_number}</span>
      <span className="history-status">{item.status}</span>
      <span className="history-time">{formatDateTime(item.started_at)}</span>
      {item.status === 'success' && item.remote_path && (
        <span className="history-path" title={item.remote_path}>
          {item.remote_path.length > 20
            ? '...' + item.remote_path.slice(-20)
            : item.remote_path}
        </span>
      )}
      {item.status === 'failed' && item.error_message && (
        <span className="history-error" title={item.error_message}>
          {item.error_message.length > 25
            ? item.error_message.substring(0, 25) + '...'
            : item.error_message}
        </span>
      )}
    </li>
  );
}

ExportHistoryItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    attempt_number: PropTypes.number,
    status: PropTypes.string,
    started_at: PropTypes.string,
    completed_at: PropTypes.string,
    error_message: PropTypes.string,
    remote_path: PropTypes.string,
  }),
};

export default function ExportStatusPanel({ imageId, photoStatus }) {
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [expandedHistory, setExpandedHistory] = useState({});

  const loadExports = useCallback(async (includeHistory = false) => {
    if (!imageId) return;
    setLoading(true);
    try {
      const data = await fetchPhotoExports(imageId, includeHistory);
      setExports(data);
    } catch {
      console.error('Failed to load exports');
    } finally {
      setLoading(false);
    }
  }, [imageId]);

  useEffect(() => {
    // Load with history when expanded, without when collapsed
    loadExports(expanded);
  }, [loadExports, photoStatus, expanded]);

  // Auto-refresh when there are pending/in_progress exports
  useEffect(() => {
    const hasPending = exports.some(e => e.status === 'pending' || e.status === 'in_progress');
    if (!hasPending) return;

    const interval = setInterval(() => loadExports(expanded), 5000);
    return () => clearInterval(interval);
  }, [exports, loadExports, expanded]);

  const handleRetry = async (exportId) => {
    setRetrying(exportId);
    try {
      await retryPhotoExport(imageId, exportId);
      await loadExports(expanded);
    } catch {
      console.error('Failed to retry export');
    } finally {
      setRetrying(null);
    }
  };

  const handleRetryAll = async () => {
    setRetrying('all');
    try {
      await retryAllPhotoExports(imageId);
      await loadExports(expanded);
    } catch {
      console.error('Failed to retry all exports');
    } finally {
      setRetrying(null);
    }
  };

  const toggleHistory = (exportId) => {
    setExpandedHistory((prev) => ({
      ...prev,
      [exportId]: !prev[exportId],
    }));
  };

  // Only show for photos with done/exported status
  if (!['done', 'exported'].includes(photoStatus)) {
    return null;
  }

  // Don't render if no exports
  if (exports.length === 0 && !loading) {
    return null;
  }

  const failedCount = exports.filter(e => e.status === 'failed').length;
  const successCount = exports.filter(e => e.status === 'success').length;
  const pendingCount = exports.filter(e => e.status === 'pending' || e.status === 'in_progress').length;
  const cancelledCount = exports.filter(e => e.status === 'cancelled').length;
  const activeCount = exports.length - cancelledCount;

  // Summary status
  let summaryStatus = 'pending';
  let summaryText = 'Exporting...';
  const cancelledSuffix = cancelledCount > 0 ? ` (${cancelledCount} cancelled)` : '';

  if (activeCount === 0 && cancelledCount > 0) {
    summaryStatus = 'cancelled';
    summaryText = `${cancelledCount} cancelled`;
  } else if (successCount === activeCount && activeCount > 0) {
    summaryStatus = 'success';
    summaryText = `Exported to ${successCount} target${successCount !== 1 ? 's' : ''}${cancelledSuffix}`;
  } else if (failedCount > 0 && pendingCount === 0) {
    summaryStatus = 'failed';
    summaryText = `${failedCount} failed${cancelledSuffix}`;
  } else if (pendingCount > 0) {
    summaryStatus = 'pending';
    summaryText = `Exporting... (${pendingCount} pending)`;
  }

  return (
    <div className="export-status-panel">
      <div
        className={`export-summary ${STATUS_CLASSES[summaryStatus]}`}
        onClick={() => setExpanded(!expanded)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && setExpanded(!expanded)}
      >
        <span className="export-summary-icon">{STATUS_ICONS[summaryStatus]}</span>
        <span className="export-summary-text">{summaryText}</span>
        {failedCount > 0 && (
          <button
            className="retry-all-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleRetryAll();
            }}
            disabled={retrying === 'all'}
          >
            {retrying === 'all' ? 'Retrying...' : 'Retry All'}
          </button>
        )}
        <span className="expand-icon">{expanded ? '-' : '+'}</span>
      </div>

      {expanded && (
        <div className="export-details">
          {loading ? (
            <div className="export-loading">Loading...</div>
          ) : exports.length === 0 ? (
            <div className="export-empty">No FTP targets configured</div>
          ) : (
            <ul className="export-list">
              {exports.map((exp) => (
                <li key={exp.id} className={`export-item ${STATUS_CLASSES[exp.status]}`}>
                  <div className="export-item-header">
                    <span className="export-target-name">{exp.ftp_target_name}</span>
                    {exp.crop_version && exp.crop_version !== 'default' && (
                      <span className="export-version-badge">{exp.crop_version}</span>
                    )}
                    <span className="export-status-badge">{exp.status}</span>
                    {exp.status === 'failed' && (
                      <>
                        {exp.error_message && (
                          <span className="export-error" title={exp.error_message}>
                            {exp.error_message.length > 30
                              ? exp.error_message.substring(0, 30) + '...'
                              : exp.error_message}
                          </span>
                        )}
                        <button
                          className="retry-btn"
                          onClick={() => handleRetry(exp.id)}
                          disabled={retrying === exp.id}
                        >
                          {retrying === exp.id ? '...' : 'Retry'}
                        </button>
                      </>
                    )}
                    {exp.status === 'success' && exp.completed_at && (
                      <span className="export-time">
                        {new Date(exp.completed_at).toLocaleTimeString()}
                      </span>
                    )}
                    {exp.attempts_history && exp.attempts_history.length > 1 && (
                      <button
                        className="history-toggle-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleHistory(exp.id);
                        }}
                        title="Show export history"
                      >
                        {expandedHistory[exp.id] ? 'Hide' : `History (${exp.attempts_history.length})`}
                      </button>
                    )}
                  </div>
                  {exp.status === 'success' && exp.remote_path && (
                    <div className="export-remote-path" title={exp.remote_path}>
                      {exp.remote_path}
                    </div>
                  )}
                  {exp.initiated_by_username && (
                    <div className="export-initiated-by">
                      Initiated by {exp.initiated_by_username}
                    </div>
                  )}
                  {expandedHistory[exp.id] && exp.attempts_history && (
                    <ul className="export-history-list">
                      {exp.attempts_history.map((item) => (
                        <ExportHistoryItem key={item.id} item={item} />
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

ExportStatusPanel.propTypes = {
  imageId: PropTypes.string,
  photoStatus: PropTypes.string,
};
